import {
  Button, Flex,
  Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter,
  ModalHeader,
  ModalOverlay, Select, Stack,
  Text, useToast,
} from "@chakra-ui/react";
import React, {useEffect, useState} from "react";
import {getCompanyName} from "../../../../extensions/stylingExstension";
import {addConversation, updateConversation} from "../../../../api/lawdiskService";
import {useHistory} from "react-router-dom";
import {formatTimestamp} from "../../../../services/helper/general";
import {refreshAccessToken} from "../../../../services/authService";

const RecordSettingsModal = ({isOpen, onOpen, onClose, user}) => {
  const toast = useToast()
  const initialRef = React.useRef()
  const finalRef = React.useRef()
  const [loadingMicrophone, setLoadingMicrophone] = useState(true);
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState(undefined);
  const [selectedModel, setSelectedModel] = useState("generic");
  const [micStream, setMicStream] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState(0);

  function stopMicrophone() {
    if (micStream) {
      micStream.getTracks().forEach(track => track.stop());
      setMicStream(null);
    }
  }

  const history = useHistory();

  async function startRecording() {
    setIsStarted(true);
    const refreshToken =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");
    await refreshAccessToken(refreshToken);
    const conversation = await addConversation("");
    await updateConversation(conversation.id, "", `Notule van ${formatTimestamp(Date.now())}`);
    localStorage.setItem(conversation.id, JSON.stringify({
      microphoneId: selectedMicrophone,
      model: selectedModel
    }));
    stopMicrophone();
    const path = `/admin/dashboard/live-notuleren/${conversation.id}`;
    history.push(path);
  }

  async function handlePermissionDenied(closeModal = false) {
    toast({
      title: `De toegang tot de microfoon is geweigerd`,
      description: `Sta het microfoon gebruik voor ${getCompanyName()} toe in uw browser om te kunnen notuleren.`,
      status: `error`,
      duration: 10000,
      isClosable: true,
    });

    if (closeModal) {
      onClose();
    }
  }

  async function initMicrophones() {
    try {
      const permissionStatus = await navigator.permissions.query({name: 'microphone'});
      if (permissionStatus.state === 'denied') {
        await handlePermissionDenied();
      }

      permissionStatus.onchange = () => {
        if (permissionStatus.state === 'denied') {
          handlePermissionDenied(true);
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      setMicStream(stream);

      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      setAvailableMicrophones(microphones);
    } catch (err) {
      console.error('Error accessing microphone:', err);
    } finally {
      setLoadingMicrophone(false);
    }
  }

  useEffect(() => {
    if (
      availableMicrophones.length > 0 &&
      !selectedMicrophone
    ) {
      setSelectedMicrophone(availableMicrophones[0].deviceId);
    }
  }, [availableMicrophones, selectedMicrophone]);

  const handleClose = () => {
    setIsStarted(false);
    stopMicrophone();
    onClose();
  };


  useEffect(() => {
    if (isOpen) {
      initMicrophones();
    }
  }, [isOpen]);

  useEffect(() => {
    if(user !== null){
      setIsPremiumUser(user.hasSubscription);
      if(user.hasSubscription === false){
        const remainingSeconds = user.freemiumSeconds;
        const remainingMinutes = Math.floor(remainingSeconds / 60);
        setRemainingMinutes(remainingMinutes);
      }
    }
  }, [user])

  return (
    <>
      <Modal
        size='lg'
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isOpen}
        onClose={handleClose}
      >
        <ModalOverlay/>
        <ModalContent borderRadius="lg" maxW="md">
          <ModalHeader fontWeight="bold">Realtime notuleren</ModalHeader>
          <ModalCloseButton/>

          <ModalBody>
            {!isPremiumUser &&
              (<Flex>
                <Text fontWeight={"bold"} fontSize={"md"} color={'red.300'}>
                  LET OP! U bent momenteel geen premium gebruiker, u heeft nog {remainingMinutes} minuten om gratis te notuleren.
                </Text>
              </Flex>)
            }
            <Flex py={2}>
              <Text fontWeight={"light"} fontStyle={"italic"} fontSize={"sm"}>Hier kiest u het model en de microfoon
                welke u tijdens uw
                notulen wilt
                gebruiken.</Text>
            </Flex>

            <Stack spacing={2}>
              <Text fontWeight={"bold"}>Selecteer uw model </Text>
              <Select mt={2}
                      value={selectedModel}
                      onChange={(value) => setSelectedModel(value.target.value)}
              >
                <option
                  key="generic"
                  value="generic">
                  Generiek
                </option>
                <option
                  key="kolibri"
                  value="kolibri">
                  Ooievaar [expirimenteel]
                </option>
                <option
                  key="multilingual"
                  value="multilingual">
                  Meertalig
                </option>
              </Select>
            </Stack>

            <Stack spacing={2}>
              <Text py={2} fontWeight={"bold"}>Selecteer uw microfoon 🎤</Text>
              <Select mt={2}
                      disabled={loadingMicrophone}
                      value={selectedMicrophone ?? "default"}
                      onChange={(value) => setSelectedMicrophone(value.target.value)}
              >
                {
                  (!loadingMicrophone && availableMicrophones.map(device => (
                    <option
                      key={device.deviceId}
                      value={device.deviceId}
                    >
                      {device.label}
                    </option>
                  )))
                }

              </Select>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button
              isLoading={loadingMicrophone || isStarted}
              isDisabled={isStarted}
              textColor="white"
              bg="var(--primary-color)" w="100%"
              onClick={async () => {
                await startRecording();
              }}
              _hover={{
                bg: "rgba(255, 0, 0, 0.3)",
              }}
            >
              Start met notuleren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RecordSettingsModal;
